import { getStore } from 'shared/store';
import { getDomainConfig } from 'shared/domainConfig/helpers';
import { BACKEND_STORE_PREFIX, DEFAULT_STORE } from 'shared/constants/store';
export { BACKEND_STORE_PREFIX, DEFAULT_STORE } from 'shared/constants/store';

/**
 * Derive known store prefixes from remoteConfig.storeConfigs (single source of truth).
 * Falls back to an empty array when the Redux store is not yet initialised.
 */
const getKnownStorePrefixes = (): string[] => {
  try {
    const store = getStore();
    const state = store?.getState();
    const storeConfigs: Array<{ storeId: string; }> = state?.remoteConfigs?.storeConfigs || [];
    return storeConfigs.map(({ storeId }) => `/${storeId}`);
  } catch {
    return [];
  }
};

/**
 * Get the store path prefix based on storeId
 * @param storeId - The store identifier (e.g., 'luxe', 'genz', 'web')
 * @returns The store path prefix (e.g., '/luxe', '/genz') or empty string for web store
 */
export const getStorePathPrefix = (storeId: string): string => {
  if (!storeId || storeId === DEFAULT_STORE) {
    return '';
  }

  return `/${storeId}`;
};

/**
 * Get the current store ID from Redux state
 * @returns The current store ID (defaults to 'web')
 */
export const getCurrentStoreId = (): string => {
  try {
    const store = getStore();
    const state = store?.getState();
    return state?.app?.storeId || DEFAULT_STORE;
  } catch (error) {
    // Fallback to web if store is not available (e.g., during SSR or before store initialization)
    return DEFAULT_STORE;
  }
};

/**
 * Strip any known store prefix from a path
 * Used when user is in DEFAULT_STORE to avoid navigating into another store
 */
const stripKnownStorePrefix = (path: string): string => {
  const knownStorePrefixes = getKnownStorePrefixes();
  for (const prefix of knownStorePrefixes) {
    if (path.startsWith(prefix + '/')) {
      return path.slice(prefix.length);
    }
    if (path === prefix) {
      return '/';
    }
  }
  return path;
};

/**
 * Check if a URL is external (starts with http:// or https://)
 * @param url - The URL to check
 * @returns True if the URL is external
 */
const isExternalUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Check if a path already has a store prefix
 * @param path - The path to check
 * @param storeId - The store identifier
 * @returns True if the path already starts with the store prefix
 */
const hasStorePrefix = (path: string, storeId: string): boolean => {
  if (!storeId || storeId === DEFAULT_STORE) {
    return false;
  }
  const prefix = getStorePathPrefix(storeId);
  if (!prefix) return false;
  return path.startsWith(prefix + '/') || path === prefix;
};

/**
 * Prepend store path prefix to a URL path
 * This function automatically detects the current store and adds the appropriate prefix
 *
 * @param path - The URL path to prepend the store prefix to
 * @param storeId - Optional store ID. If not provided, will use current store from Redux
 * @returns The path with store prefix prepended (e.g., '/some/path' -> '/luxe/some/path')
 *
 * @example
 * // If current store is 'luxe'
 * getStoreUrl('/products') // Returns: '/luxe/products'
 * getStoreUrl('/luxe/products') // Returns: '/luxe/products' (already prefixed)
 * getStoreUrl('https://example.com') // Returns: 'https://example.com' (external URL)
 *
 * // If current store is 'web'
 * getStoreUrl('/products') // Returns: '/products' (no prefix for web)
 *
 * // With explicit storeId
 * getStoreUrl('/products', 'genz') // Returns: '/genz/products'
 */
export const getStoreUrl = (path: string, storeId?: string): string => {
  // Use provided storeId or get current store
  const currentStoreId = storeId ? extractStoreKey(storeId) : getCurrentStoreId();

  // Don't modify external URLs
  if (isExternalUrl(path)) {
    return path;
  }

  // Don't add prefix for web store — but strip any other store prefix
  if (!currentStoreId || currentStoreId === DEFAULT_STORE) {
    return stripKnownStorePrefix(path);
  }

  // Don't add prefix if already present
  if (hasStorePrefix(path, currentStoreId)) {
    return path;
  }

  // Add the store prefix
  const prefix = getStorePathPrefix(currentStoreId);

  if (!prefix) {
    return path;
  }

  // Handle paths that don't start with '/'
  if (!path.startsWith('/')) {
    path = '/' + path;
  }

  return prefix + path;
};

/**
 * Strip store path prefix from a URL path
 * Useful for getting the base path without store prefix
 *
 * @param path - The URL path to strip the store prefix from
 * @param storeId - Optional store ID. If not provided, will use current store from Redux
 * @returns The path without store prefix (e.g., '/luxe/some/path' -> '/some/path')
 *
 * @example
 * stripStoreUrl('/luxe/products', 'lux') // Returns: '/products'
 * stripStoreUrl('/products', 'lux') // Returns: '/products' (no prefix to strip)
 */
export const stripStoreUrl = (path: string, storeId?: string): string => {
  // Use provided storeId or get current store
  const currentStoreId = storeId || getCurrentStoreId();

  // Don't modify external URLs
  if (isExternalUrl(path)) {
    return path;
  }

  // Don't strip for 'web' store
  if (!currentStoreId || currentStoreId === DEFAULT_STORE) {
    return path;
  }

  // Check if path has the store prefix
  if (!hasStorePrefix(path, currentStoreId)) {
    return path;
  }

  // Strip the prefix
  const prefix = getStorePathPrefix(currentStoreId);
  const strippedPath = path.substring(prefix.length);

  // Ensure path starts with '/'
  return strippedPath || '/';
};

// Extract the store key from an API store redirect value.
// Handles both 'fashion_luxe' -> 'luxe' and plain 'luxe' -> 'luxe'
export const extractStoreKey = (storeRedirect: string): string => {
  const prefix = getDomainConfig().CATALOG_TAG_PREFIX;
  if (!prefix) return storeRedirect;
  return storeRedirect.startsWith(prefix)
    ? storeRedirect.slice(prefix.length)
    : storeRedirect;
};

// Build the store redirect identifier expected by the API.
// Returns null for domains that don't use catalog tag filtering (e.g. Foot Locker).
export const buildStoreRedirectId = (storeId: string = DEFAULT_STORE): string | null => {
  const prefix = getDomainConfig().CATALOG_TAG_PREFIX;
  if (!prefix) return null;
  if (!storeId) return `${prefix}${DEFAULT_STORE}`;
  if (storeId.startsWith(prefix)) return storeId;
  return `${prefix}${storeId}`;
};

const DEFAULT_CATALOG_TAGS = new Set(['fashion', BACKEND_STORE_PREFIX + DEFAULT_STORE]); // "fashion", "fashion_web"



/**
 * Specifically for search suggestion API
 * Returns the target storeId if the suggestion belongs to a different store than the current one,
 * or null if they are the same store (no cross-store redirect needed).
 *
 * @example
 * // current store = 'web', catalog_tag = 'fashion_luxe' → returns 'luxe'
 * // current store = 'luxe', catalog_tag = 'fashion_luxe' → returns null (same store)
 * // current store = 'luxe', catalog_tag = undefined      → returns 'web'
 */
export const getCatalogTagStoreId = (catalog_tag: string | undefined, currentStoreId: string): string | null => {
  const suggestionStoreId = (!catalog_tag || catalog_tag === 'fashion')
    ? DEFAULT_STORE
    : extractStoreKey(catalog_tag);

  return suggestionStoreId !== currentStoreId ? suggestionStoreId : null;
};


export const resolveUrlFromCatalogTag = (suggestion: { url: string; catalog_tag?: string; }): string => {
  const { catalog_tag, url } = suggestion;
  if (!catalog_tag || DEFAULT_CATALOG_TAGS.has(catalog_tag)) return url;

  // 1. Prefer the backend-provided store-specific URL (e.g. fashion_luxe_url)
  const storeUrlKey = `${catalog_tag}_url`;
  const explicitUrl = (suggestion as Record<string, unknown>)[storeUrlKey] as string | undefined;
  if (explicitUrl) return explicitUrl;

  // 2. Backend did not supply a dedicated URL (common for category suggestions).
  //    Construct the target store URL by prepending the store path prefix to the base URL.
  const targetStoreId = extractStoreKey(catalog_tag);
  return getStoreUrl(url, targetStoreId);
};
